import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useFeedNeeds } from './useFeedNeeds';
import { useMarketStore } from '@/stores/marketStore';
import type { UsPositionMarket } from '@/lib/polymarketUs/positionEconomics';
import type { UsDiscoveryMarket } from '@/lib/polymarketUs/marketClient';

export type UsPositionMarks = { getMarket(slug: string): UsPositionMarket | null };
type Catalogue = { getMarket(slug: string): UsDiscoveryMarket | null };

// Only mounted inventory slugs are retained. A feed reset/removal invalidates
// the old quote's currentAt closure; keeping that object prevents an older REST
// snapshot from resurfacing after the shared store removes the row altogether.
class PositionMarks {
  private seen = new Map<string, UsPositionMarket>();
  private lastVersion = -1;
  private lastCatalogueVersion = -1;
  private lastSlugs: readonly string[] | null = null;
  private value: UsPositionMarks = { getMarket: () => null };
  constructor(private catalogue: Catalogue) {}
  snapshot(slugs: readonly string[], catalogueVersion: number): UsPositionMarks {
    const store = useMarketStore.getState();
    if (this.lastVersion === store.version && this.lastCatalogueVersion === catalogueVersion && this.lastSlugs === slugs) return this.value;
    this.lastVersion = store.version; this.lastCatalogueVersion = catalogueVersion; this.lastSlugs = slugs;
    const allowed = new Set(slugs);
    for (const slug of this.seen.keys()) if (!allowed.has(slug)) this.seen.delete(slug);
    const quotes = new Map(slugs.map(slug => {
      const streamed = store.getUsPositionQuote(slug);
      if (streamed) this.seen.set(slug, streamed);
      return [slug, streamed ?? this.seen.get(slug) ?? this.catalogue.getMarket(slug)] as const;
    }));
    return this.value = { getMarket: slug => quotes.get(slug) ?? null };
  }
}
const subscribe = (notify: () => void) => useMarketStore.subscribe((state, before) => {
  if (state.version !== before.version) notify();
});

/** Reuse the existing Hydra socket/store for held US market marks, just as
 * Intl positions do. No new socket, server timer, per-row read or venue call.
 * A present but withdrawn/expired browser quote overrides the catalogue:
 * stale continuity must never be concealed by a previously loaded price.
 * This is display evidence only; no ticket/readiness code consumes it. */
export function useUsPositionMarks(sourceId: string, slugs: readonly string[],
  catalogue: Catalogue, catalogueVersion: number): UsPositionMarks {
  const exactSlugs = useMemo(() => [...new Set(slugs.filter(slug => /^[A-Za-z0-9_-]{1,256}$/.test(slug)))], [slugs]);
  const reader = useMemo(() => new PositionMarks(catalogue), [catalogue]);
  const snapshot = useCallback(() => reader.snapshot(exactSlugs, catalogueVersion), [reader, exactSlugs, catalogueVersion]);
  const active = exactSlugs.length > 0;
  const subscribeMarks = useCallback((notify: () => void) => active ? subscribe(notify) : () => {}, [active]);
  const marks = useSyncExternalStore(subscribeMarks, snapshot);
  const needs = useMemo(() => {
    // No held markets means no feed declaration. Passing empty axes still
    // serializes/sorts them on every parent render in useFeedNeeds.
    if (exactSlugs.length === 0) return null;
    const sports = new Set<string>(), games = new Set<string>();
    for (const slug of exactSlugs) {
      const market = marks.getMarket(slug) ?? catalogue.getMarket(slug);
      if (!market) continue;
      sports.add(market.sport);
      // Prefer producer wire keys when already known; a catalogue's game key
      // supplies cold-start identity. None of this grants quote authority.
      const wire = useMarketStore.getState().getGameWireKeys(market.gameKey);
      for (const key of wire.length ? wire : [market.gameKey]) games.add(key);
    }
    return { tickers: exactSlugs.map(slug => `PMUS-${slug}`), sports, games, lineScope: 'any' as const };
  }, [exactSlugs, marks, catalogue]);
  useFeedNeeds(sourceId, needs);
  return marks;
}
